import Uppy, { Meta, UppyFile } from '@uppy/core';
import '@uppy/core/dist/style.css';
import '@uppy/file-input/dist/style.css';
import { FileInput } from '@uppy/react';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { useOrganizationContext } from '@/contexts/organization-context';
import { getMediaPath, getUppy } from '@/lib/uppy.util';
import MediaElement from '../media-element';
import { Progress } from '@/components/shadcn/progress';
import { Media } from '@/__generated__/graphql';

type PreviewType = { fileId: string; previewUrl?: string };

export enum AssetsType {
  assets = 'assets',
  posts = 'posts',
  logos = 'logos',
}

const UppyUploader: FC<{
  onUploadingStarted: () => void;
  onUploadFileReady?: (fileId: string, url: string) => void;
  onUploadError?: (fileName: string) => void;
  onCancelUploading?: () => void; // TODO add a cancel button
  isDashboard?: boolean;
  isDisabled?: boolean;
  assetsType?: AssetsType;
  children?: ReactNode;
}> = ({
  onUploadingStarted,
  onUploadFileReady,
  onUploadError,
  isDashboard,
  isDisabled,
  assetsType,
  children,
}) => {
  const session = useSessionContext();
  const [progressState, setProgress] = useState<number>(0);
  const [previews, setPreviews] = useState<PreviewType[]>([]);
  const [uppy, setUppy] = useState<Uppy>();
  const { organizationId } = useOrganizationContext();
  const inputText =
    assetsType === AssetsType.logos ? 'Add logos' : 'Add image or video';

  useEffect(() => {
    if (
      !session.loading &&
      session.doesSessionExist &&
      session.accessTokenPayload['st-role']?.v
    ) {
      setUppy(getUppy());
    }
  }, [session]);

  const onUploadSuccess = (
    file: UppyFile<Meta, Record<string, Media>> | undefined,
    response: any
  ) => {
    const mediaId = getMediaPath(response.uploadURL);
    if (!mediaId) {
      onUploadError &&
        onUploadError(
          `file name:'${file?.name || ''}' upload url:'${response.uploadURL || ''}'`
        );
      setPreviews((previews) =>
        previews.filter((preview) => preview.fileId !== file?.id)
      );
      return;
    }

    onUploadFileReady && onUploadFileReady(mediaId, response.uploadURL || '');
    setPreviews((previews) =>
      previews.filter((preview) => preview.fileId !== file?.id)
    );
  };
  const onUploadErrorEvent = (
    file: UppyFile<Meta, Record<string, Media>> | undefined,
    error: Error,
    response?: any
  ) => {
    onUploadError && onUploadError(file?.name || 'file');
    setPreviews((previews) =>
      previews.filter((preview) => preview.fileId !== file?.id)
    );
  };

  const onProgress = (progress: number) => {
    onUploadingStarted && onUploadingStarted();
    setProgress(progress);
  };
  const onThumbnail = (
    file: UppyFile<Meta, Record<string, Media>>,
    previewUrl: string
  ) => {
    onUploadingStarted && onUploadingStarted();
    if (!file?.response?.uploadURL) {
      setPreviews((previews) =>
        previews
          .filter((item) => item.fileId !== file.id)
          .concat({ fileId: file.id, previewUrl })
      );
    }
  };
  const onUploadProgress = (
    file: UppyFile<Meta, Record<string, Media>> | undefined,
    progress: any
  ) => {
    onUploadingStarted && onUploadingStarted();
    if (file?.id) {
      setPreviews((previews) =>
        previews.some((item) => item.fileId === file?.id)
          ? previews
          : previews.concat({ fileId: file?.id })
      );
    }
  };

  useEffect(() => {
    if (uppy) {
      const metaOptions = {
        ...(organizationId && {
          org_id: organizationId,
        }),
        ...(assetsType && { assets_type: assetsType }),
      };

      uppy.setOptions({ meta: metaOptions });
      uppy.on('upload-success', onUploadSuccess);
      uppy.on('upload-error', onUploadErrorEvent);
      if (!isDashboard) {
        uppy.on('progress', onProgress);
        uppy.on('upload-progress', onUploadProgress);
        uppy.on('thumbnail:generated', onThumbnail);
      }
      return () => {
        uppy.off('upload-success', onUploadSuccess);
        uppy.off('upload-error', onUploadErrorEvent);
        if (!isDashboard) {
          uppy.off('progress', onProgress);
          uppy.off('upload-progress', onUploadProgress);
          uppy.off('thumbnail:generated', onThumbnail);
        }
      };
    }
  }, [uppy]);

  useEffect(() => {
    if (!previews?.length) {
      setProgress(0);
    }
  }, [previews]);

  if (isDashboard && children) {
    return <>{children}</>;
  }

  return (
    <div>
      {!uppy ? (
        <></>
      ) : (
        <div
          className='mt-4'
          onClick={(event: React.MouseEvent) =>
            isDisabled && event.preventDefault()
          }
        >
          <FileInput
            uppy={uppy}
            locale={{
              strings: { chooseFiles: inputText },
              pluralize: () => 0,
            }}
          />
        </div>
      )}
      {!!previews?.length && (
        <>
          <p>Uploading ...</p>
          <Progress value={progressState} className='mb-2 mt-2' />
          <div className='border-lightgray border'>
            <div className='m-2 flex'>
              {previews.map((preview) => (
                <MediaElement key={preview.fileId} url={preview.previewUrl} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UppyUploader;
